import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LuCircleAlert, LuX } from 'react-icons/lu';

const isPWA = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function NoShowConfirmSheet({
  open,
  guest,
  getGuestName,
  saving = false,
  onClose,
  onConfirm,
}) {
  const scrollYRef = useRef(0);

  useEffect(() => {
    if (!open) return;

    const meta = document.querySelector('meta[name="theme-color"]');

    scrollYRef.current =
      window.scrollY ||
      window.pageYOffset ||
      0;

    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.classList.add('overlay-active');
    document.documentElement.classList.add('overlay-active');
    document
      .getElementById('root')
      ?.classList.add('overlay-active');

    if (meta) {
      meta.content = '#999998';
    }

    return () => {
      const y = scrollYRef.current;

      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document
        .getElementById('root')
        ?.classList.remove('overlay-active');

      document.body.style.top = '';

      window.scrollTo(0, y);

      const nextMeta = document.querySelector(
        'meta[name="theme-color"]'
      );

      if (nextMeta) {
        nextMeta.content = '#ffffff';
      }
    };
  }, [open]);

  if (!open || !guest) {
    return null;
  }

  const name = getGuestName(guest);

  const sheet = (
    <div
      style={S.backdrop}
      onClick={saving ? undefined : onClose}
    >
      <div
        className="no-show-confirm-sheet"
        style={{
          ...S.sheet,

          paddingBottom: `calc(
            12px +
            env(safe-area-inset-bottom, 0px) +
            ${isPWA() ? 8 : 0}px
          )`,
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Mark guest as no-show"
      >
        <div style={S.header}>
          <div style={S.headerTitle}>
            Confirm No-Show
          </div>

          <button
            type="button"
            aria-label="Close"
            style={{
              ...S.closeBtn,
              cursor: saving
                ? 'default'
                : 'pointer',
              opacity: saving ? 0.5 : 1,
            }}
            onClick={onClose}
            disabled={saving}
          >
            <LuX size={17} />
          </button>
        </div>

        <div style={S.body}>
          <div style={S.iconWrap}>
            <LuCircleAlert
              size={28}
              strokeWidth={2.4}
            />
          </div>

          <div style={S.content}>
            <div style={S.dangerPill}>
              Attendance update
            </div>

            <h2 style={S.sheetTitle}>
              Mark as no-show?
            </h2>

            <p style={S.sheetText}>
              This will mark{' '}
              <strong
                style={{
                  color: '#0b0b0b',
                  fontWeight: 850,
                }}
              >
                {name}
              </strong>{' '}
              as not having shown up.
            </p>

            <div style={S.warningBox}>
              <p style={S.warningText}>
                Only mark{' '}
                <strong
                  style={{
                    color: '#991b1b',
                    fontWeight: 900,
                  }}
                >
                  {name}
                </strong>{' '}
                as a no-show if they did not attend the
                gathering. If they arrive later, undo the
                no-show.
              </p>
            </div>
          </div>
        </div>

        <div style={S.footer}>
          <div style={S.footerRow}>
            <button
              type="button"
              style={{
                ...S.secondaryBtn,
                cursor: saving
                  ? 'default'
                  : 'pointer',
                opacity: saving ? 0.55 : 1,
              }}
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="button"
              style={{
                ...S.confirmBtn,
                cursor: saving
                  ? 'default'
                  : 'pointer',
                opacity: saving ? 0.65 : 1,
              }}
              onClick={onConfirm}
              disabled={saving}
            >
              {saving ? (
                <span style={S.confirmBtnContent}>
                  <span style={S.spinner} />
                  Marking
                </span>
              ) : (
                'Mark no-show'
              )}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes slideUp {
            from {
              transform: translateY(16px);
              opacity: 0;
            }

            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          @keyframes spin {
            from {
              transform: rotate(0deg);
            }

            to {
              transform: rotate(360deg);
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .no-show-confirm-sheet,
            .no-show-confirm-sheet * {
              animation: none !important;
              transition: none !important;
            }
          }

          .no-show-confirm-sheet button:focus-visible {
            outline: 0;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.45);
          }
        `}</style>
      </div>
    </div>
  );

  return createPortal(
    sheet,
    document.body
  );
}

const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 9999,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 8,

    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  sheet: {
    width: '100%',
    maxWidth: 440,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .22s ease-out',
  },

  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    padding: '0 12px',
    borderBottom: '1px solid #eef0f2',
  },

  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },

  closeBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center',
    lineHeight: 0,
    color: '#0b0b0b',
    cursor: 'pointer',
    padding: 0,
  },

  body: {
    padding: '18px 12px 14px',
  },

  iconWrap: {
    width: 58,
    height: 58,
    borderRadius: 999,
    background: '#fef2f2',
    color: '#991b1b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 14px',
  },

  content: {
    textAlign: 'center',
  },

  dangerPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '7px 12px',
    borderRadius: 999,
    background: '#fee2e2',
    color: '#991b1b',
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.025em',
    marginBottom: 10,
  },

  sheetTitle: {
    margin: 0,
    fontSize: 22,
    lineHeight: 1.12,
    fontWeight: 950,
    color: '#0b0b0b',
    letterSpacing: '-0.045em',
  },

  sheetText: {
    margin: '7px auto 0',
    maxWidth: 330,
    fontSize: 14,
    lineHeight: 1.45,
    color: '#4b5563',
    fontWeight: 500,
  },

  warningBox: {
    margin: '14px auto 0',
    maxWidth: 340,
    borderRadius: 12,
    border: '1px solid #fee2e2',
    background: '#fff',
    padding: 12,
    textAlign: 'left',
  },

  warningText: {
    margin: 0,
    fontSize: 12.5,
    lineHeight: 1.4,
    fontWeight: 650,
    color: '#991b1b',
  },

  footer: {
    padding: 12,
  },

  footerRow: {
    display: 'flex',
    gap: 8,
  },

  secondaryBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0b0b0b',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },

  confirmBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    border: '1px solid #991b1b',
    background: '#991b1b',
    color: '#fff',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },

  confirmBtnContent: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    whiteSpace: 'nowrap',
  },

  spinner: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.35)',
    borderTopColor: '#fff',
    animation: 'spin 720ms linear infinite',
    flexShrink: 0,
  },
};